// Dialog de confirmação da conversão proposta → assinatura (Story 11.9 AC8).
// ADAPT de RejectProposalDialog (mesmo padrão Radix Dialog + PillButton).
//
// Dois modos:
//  - Confirmação: avisa que cliente + assinatura serão criados (ação irreversível).
//  - missingFields: quando o backend retorna 400 MISSING_CLIENT_DATA, lista os
//    campos faltantes com link pra completar o lead/cliente (não inventa dado).

import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, X } from 'lucide-react';

import { cn } from '@control-center/ui';

import { PillButton } from '../clients/PillButton';

/** Rótulos humanos pros campos faltantes retornados pelo backend (AC3). */
const MISSING_FIELD_LABELS: Record<string, string> = {
  name: 'Nome do cliente',
  document: 'CPF/CNPJ (o plano gera cobrança imediata)',
  phone_or_email: 'Telefone ou e-mail (para notificação)',
  client_or_lead: 'Cliente ou lead vinculado',
};

interface ConvertProposalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposalNumber: string;
  isPending?: boolean;
  /** Campos faltantes (AC3) — quando preenchido, mostra o modo de correção. */
  missingFields: string[] | null;
  /** Link pra completar os dados do lead/cliente (quando há missingFields). */
  onEditLead?: () => void;
  onConfirm: () => void;
}

export function ConvertProposalDialog({
  open,
  onOpenChange,
  proposalNumber,
  isPending,
  missingFields,
  onEditLead,
  onConfirm,
}: ConvertProposalDialogProps): JSX.Element {
  const hasMissing = missingFields !== null && missingFields.length > 0;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          aria-describedby="convert-proposal-desc"
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(460px,calc(100vw-32px))] rounded-[14px] border border-line bg-panel shadow-lg',
            'p-7 focus:outline-none',
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-[20px] font-semibold tracking-[-0.01em] text-ink">
                {hasMissing ? 'Faltam dados para converter' : 'Converter em assinatura'}
              </Dialog.Title>
              <p id="convert-proposal-desc" className="mt-2 text-[13px] leading-[1.55] text-muted">
                {hasMissing ? (
                  <>
                    Não dá para gerar a assinatura de{' '}
                    <span className="font-medium text-ink-2">{proposalNumber}</span> sem completar
                    os dados abaixo. Edite o lead/cliente e tente de novo.
                  </>
                ) : (
                  <>
                    Ao converter <span className="font-medium text-ink-2">{proposalNumber}</span>,
                    um cliente e uma assinatura serão criados automaticamente. Esta ação não pode
                    ser desfeita.
                  </>
                )}
              </p>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          {hasMissing ? (
            <ul className="mt-5 flex flex-col gap-2">
              {missingFields.map((field) => (
                <li
                  key={field}
                  className="flex items-center gap-2 rounded-[8px] border border-warn/40 bg-warn/5 px-3 py-2 text-[13px] text-ink-2"
                >
                  <AlertTriangle size={14} strokeWidth={2} className="shrink-0 text-warn" />
                  {MISSING_FIELD_LABELS[field] ?? field}
                </li>
              ))}
            </ul>
          ) : null}

          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild>
              <PillButton>Cancelar</PillButton>
            </Dialog.Close>
            {hasMissing ? (
              onEditLead && (
                <PillButton solid onClick={onEditLead}>
                  Completar dados
                </PillButton>
              )
            ) : (
              <PillButton solid disabled={isPending} onClick={onConfirm}>
                {isPending ? 'Convertendo…' : 'Converter agora'}
              </PillButton>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
